import {
  faChartBar,
  faDownload,
  faEye,
  faFileLines,
  faRotateRight,
  faSearch,
  faStar,
  faTimes,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  buildEmptySurveyOverview,
  downloadSurveyCsv,
  fetchEventSurvey,
  fetchSurveyOverview,
} from "../../services/SurveyService";
import "./SurveyOverview.css";

const PAGE_SIZE = 8;

function formatDateTime(dateTime) {
  if (!dateTime) {
    return "-";
  }

  return new Date(dateTime).toLocaleString("hu-HU");
}

function average(values) {
  if (!values.length) {
    return null;
  }

  return values.reduce((sum, value) => sum + Number(value || 0), 0) / values.length;
}

function getAnswerValue(response, question) {
  return (
    response?.answersByQuestionId?.[question.questionId] ??
    response?.answers?.[question.answerKey] ??
    response?.answers?.[question.sourceId] ??
    response?.answers?.[question.id] ??
    response?.answers?.[question.title] ??
    ""
  );
}

function hasAnswer(value) {
  return value !== undefined && value !== null && value !== "";
}

function isScaleQuestion(question) {
  return question.type === "scale";
}

function isChoiceQuestion(question) {
  return question.type === "choice";
}

function comparableValue(value) {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLocaleLowerCase("hu-HU");
}

function getScaleRange(question) {
  const min = Number(question.scaleMin || question.min || 1);
  const max = Number(question.scaleMax || question.max || 5);

  return Array.from({ length: Math.max(max - min + 1, 1) }, (_, index) => min + index);
}

function getScaleSentiment(question, value) {
  const numericValue = Number(value);
  const range = getScaleRange(question);
  const min = range[0];
  const max = range[range.length - 1];

  if (!hasAnswer(value) || Number.isNaN(numericValue) || max === min) {
    return "neutral";
  }

  const ratio = (numericValue - min) / (max - min);

  if (ratio >= 0.72) {
    return "positive";
  }

  if (ratio >= 0.45) {
    return "warning";
  }

  return "negative";
}

function getAnsweredQuestionCount(response, questions) {
  return questions.filter((question) => hasAnswer(getAnswerValue(response, question))).length;
}

function SummaryCard({ icon, label, value, helper }) {
  return (
    <div className="survey-summary-card">
      <div className="survey-summary-icon">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div>
        <div className="survey-summary-label">{label}</div>
        <div className="survey-summary-value">{value}</div>
        {helper ? <div className="survey-summary-helper">{helper}</div> : null}
      </div>
    </div>
  );
}

function SurveyQuestionPreview({ question, response }) {
  const value = getAnswerValue(response, question);
  const sentiment = getScaleSentiment(question, value);
  const scaleRange = getScaleRange(question);
  const hasValidScaleAnswer =
    hasAnswer(value) && scaleRange.includes(Number(value));
  const selectedChoice = (question.options || []).find(
    (option) => comparableValue(option) === comparableValue(value)
  );
  const typeLabel = isScaleQuestion(question)
    ? `${scaleRange[0]}-${scaleRange[scaleRange.length - 1]} skála`
    : isChoiceQuestion(question)
      ? "Választás"
      : "Szöveg";

  return (
    <div className="survey-form-question">
      <div className="survey-form-question-header">
        <div className="survey-form-question-title">{question.title}</div>
        <span>{typeLabel}</span>
      </div>
      {isScaleQuestion(question) ? (
        <div>
          <div className={`survey-scale-choice-list is-${sentiment}`}>
            {scaleRange.map((score) => (
              <div
                className={`survey-scale-choice ${
                  Number(value) === score ? "is-selected" : ""
                }`}
                key={`${question.id}-${score}`}
              >
                {score}
              </div>
            ))}
          </div>
          {(question.scaleMinLabel || question.scaleMaxLabel) ? (
            <div className="survey-scale-label-row">
              <span>{question.scaleMinLabel || scaleRange[0]}</span>
              <span>{question.scaleMaxLabel || scaleRange[scaleRange.length - 1]}</span>
            </div>
          ) : null}
          {!hasValidScaleAnswer && response ? (
            <div className={hasAnswer(value) ? "survey-recorded-answer" : "survey-text-placeholder"}>
              {hasAnswer(value) ? `Rögzített válasz: ${value}` : "Nincs válasz"}
            </div>
          ) : null}
        </div>
      ) : isChoiceQuestion(question) && question.options?.length ? (
        <div>
          <div className="survey-choice-option-list">
            {question.options.map((option, index) => (
              <div
                className={`survey-choice-option ${
                  comparableValue(option) === comparableValue(value) ? "is-selected" : ""
                }`}
                key={`${question.id}-option-${index}`}
              >
                {option}
              </div>
            ))}
          </div>
          {response && hasAnswer(value) && !selectedChoice ? (
            <div className="survey-recorded-answer">Rögzített válasz: {value}</div>
          ) : null}
          {response && !hasAnswer(value) ? (
            <div className="survey-text-placeholder">Nincs válasz</div>
          ) : null}
        </div>
      ) : (
        <div className={hasAnswer(value) ? "survey-text-answer-copy" : "survey-text-placeholder"}>
          {hasAnswer(value) ? value : "Nincs válasz"}
        </div>
      )}
    </div>
  );
}

function SurveyDetailsSheet({ open, onClose, overview, selectedResponse }) {
  if (!open || !overview) {
    return null;
  }

  const title = selectedResponse
    ? selectedResponse.participantName
    : overview.surveyTitle;

  return (
    <div className="survey-sheet-layer" role="presentation">
      <button className="survey-sheet-backdrop" onClick={onClose} aria-label="Bezárás" />
      <aside className="survey-sheet" aria-label="Kérdőív részletek">
        <div className="survey-sheet-header">
          <div>
            <div className="survey-admin-eyebrow">
              {selectedResponse ? "Beküldött kérdőív" : "Kérdőív sablon"}
            </div>
            <h3>{title}</h3>
            <p>
              {selectedResponse
                ? `${selectedResponse.company || "Cég nélkül"} · ${formatDateTime(
                    selectedResponse.submittedAt
                  )}`
                : "A csatolt kérdőív válaszok nélkül."}
            </p>
          </div>
          <button className="survey-sheet-close" onClick={onClose} aria-label="Bezárás">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="survey-form-preview">
          {overview.questions.map((question, index) => (
            <div className="survey-form-item" key={question.id}>
              <div className="survey-form-index">{index + 1}</div>
              <SurveyQuestionPreview
                question={question}
                response={selectedResponse}
              />
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}

export function SurveyOverview({ event, survey, context }) {
  const [surveyOverview, setSurveyOverview] = useState(null);
  const [isSurveyLoading, setIsSurveyLoading] = useState(false);
  const [surveyMessage, setSurveyMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [responsePage, setResponsePage] = useState(1);
  const [sheetResponse, setSheetResponse] = useState(null);
  const [isTemplateSheetOpen, setIsTemplateSheetOpen] = useState(false);
  const [attachedSurvey, setAttachedSurvey] = useState(
    survey || event?.survey || event?.surveys?.[0] || null
  );

  const surveyContext = context || event;
  const hasAttachedSurvey = Boolean(attachedSurvey?.id);
  const contextTitle = context?.clubTitle || context?.eventTitle || event?.title;
  const contextLabel = context?.clubId
    ? "Klubhoz csatolva"
    : context?.eventId || event?.id
      ? "Eseményhez csatolva"
      : "Általános kérdőív";
  const effectiveSurvey = useMemo(
    () =>
      hasAttachedSurvey
        ? attachedSurvey
        : {
            title: "Nincs kérdőív csatolva",
          },
    [attachedSurvey, hasAttachedSurvey]
  );

  const loadSurveyOverview = useCallback(async () => {
    if (!survey?.id && !event?.id) {
      return;
    }

    setIsSurveyLoading(true);
    setSurveyMessage(null);

    try {
      const resolvedSurvey = survey || await fetchEventSurvey(event);
      const surveyForOverview = resolvedSurvey || {
        title: "Nincs kérdőív csatolva",
      };
      const overview = resolvedSurvey
        ? await fetchSurveyOverview(surveyForOverview, surveyContext)
        : buildEmptySurveyOverview(surveyForOverview);

      setAttachedSurvey(resolvedSurvey);
      setSurveyOverview(overview);
    } catch (error) {
      console.error(error);
      setSurveyMessage({
        type: "error",
        text: "A kérdőív áttekintés betöltése nem sikerült.",
      });
    } finally {
      setIsSurveyLoading(false);
    }
  }, [event, survey, surveyContext]);

  useEffect(() => {
    setSurveyMessage(null);
    loadSurveyOverview();
  }, [loadSurveyOverview]);

  const questions = useMemo(() => surveyOverview?.questions || [], [surveyOverview]);
  const responses = useMemo(() => surveyOverview?.responses || [], [surveyOverview]);
  const scaleQuestions = useMemo(
    () => questions.filter((question) => isScaleQuestion(question)),
    [questions]
  );

  const averageScaleScore = useMemo(() => {
    const values = scaleQuestions.flatMap((question) =>
      responses
        .map((response) => Number(getAnswerValue(response, question)))
        .filter((value) => !Number.isNaN(value) && value > 0)
    );

    return average(values);
  }, [responses, scaleQuestions]);

  const completionRate = useMemo(() => {
    if (!questions.length || !responses.length) {
      return 0;
    }

    const answered = responses.reduce(
      (sum, response) => sum + getAnsweredQuestionCount(response, questions),
      0
    );

    return Math.round((answered / (questions.length * responses.length)) * 100);
  }, [questions, responses]);

  const filteredResponses = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) {
      return responses;
    }

    return responses.filter((response) => {
      const responseText = [
        response.participantName,
        response.email,
        response.company,
        ...questions.map((question) => getAnswerValue(response, question)),
      ]
        .join(" ")
        .toLowerCase();

      return responseText.includes(normalizedSearch);
    });
  }, [questions, responses, searchTerm]);

  const responsePageCount = Math.max(1, Math.ceil(filteredResponses.length / PAGE_SIZE));
  const pagedResponses = useMemo(
    () =>
      filteredResponses.slice(
        (responsePage - 1) * PAGE_SIZE,
        responsePage * PAGE_SIZE
      ),
    [filteredResponses, responsePage]
  );
  const responseRangeStart = filteredResponses.length
    ? (responsePage - 1) * PAGE_SIZE + 1
    : 0;
  const responseRangeEnd = Math.min(responsePage * PAGE_SIZE, filteredResponses.length);

  useEffect(() => {
    setResponsePage(1);
  }, [searchTerm, surveyOverview]);

  useEffect(() => {
    if (responsePage > responsePageCount) {
      setResponsePage(responsePageCount);
    }
  }, [responsePage, responsePageCount]);

  const handleExportCsv = async () => {
    if (!surveyOverview) {
      return;
    }

    await downloadSurveyCsv(surveyOverview, surveyContext, attachedSurvey);
  };

  const isSheetOpen = Boolean(sheetResponse) || isTemplateSheetOpen;

  return (
    <section className="survey-admin-panel">
      <div className="survey-admin-header">
        <div>
          <div className="survey-admin-eyebrow">Kérdőív áttekintés</div>
          <h3 className="survey-admin-title">{surveyOverview?.surveyTitle || effectiveSurvey.title}</h3>
          <div className="survey-admin-meta">
            <span>{hasAttachedSurvey ? contextLabel : "Nincs csatolt kérdőív"}</span>
            <span>{surveyOverview?.source === "api" ? "API adat" : "Nincs beküldés adat"}</span>
            <span>{contextTitle}</span>
          </div>
        </div>
        <div className="survey-admin-actions">
          <button
            className="survey-action-button survey-action-button-secondary"
            onClick={() => setIsTemplateSheetOpen(true)}
            disabled={!surveyOverview}
          >
            <FontAwesomeIcon icon={faFileLines} />
            Kérdőív megtekintése
          </button>
          <button
            className="survey-action-button survey-action-button-secondary"
            onClick={loadSurveyOverview}
            disabled={isSurveyLoading}
          >
            <FontAwesomeIcon icon={faRotateRight} />
            {isSurveyLoading ? "Frissítés..." : "Frissítés"}
          </button>
          <button
            className="survey-action-button survey-action-button-secondary"
            onClick={handleExportCsv}
            disabled={!surveyOverview}
          >
            <FontAwesomeIcon icon={faDownload} />
            CSV
          </button>
        </div>
      </div>

      {surveyMessage ? (
        <div className={`survey-admin-message is-${surveyMessage.type}`}>{surveyMessage.text}</div>
      ) : null}

      {!hasAttachedSurvey ? (
        <div className="survey-admin-message is-info">
          Nincs kérdőív csatolva.
        </div>
      ) : null}

      {isSurveyLoading ? (
        <div className="survey-admin-loading">Kérdőív adatok betöltése...</div>
      ) : (
        <Fragment>
          <div className="survey-overview-layout">
            <div className="survey-admin-card survey-submissions-card">
              <div className="survey-table-header">
                <div>
                  <div className="survey-admin-card-title">Beküldött kérdőívek</div>
                  <div className="survey-table-count">
                    {responseRangeStart}-{responseRangeEnd} / {filteredResponses.length} kitöltés
                  </div>
                </div>
                <label className="survey-search">
                  <FontAwesomeIcon icon={faSearch} />
                  <input
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Keresés név, cég vagy válasz alapján"
                  />
                </label>
              </div>

              <div className="survey-submission-list">
                {pagedResponses.map((response) => {
                  const answeredCount = getAnsweredQuestionCount(response, questions);

                  return (
                    <button
                      className="survey-submission-item"
                      key={response.id}
                      onClick={() => setSheetResponse(response)}
                    >
                      <div className="survey-submission-avatar">
                        {response.participantName?.charAt(0) || "?"}
                      </div>
                      <div className="survey-submission-main">
                        <strong>{response.participantName}</strong>
                        <span>{response.company || "Cég nélkül"}</span>
                      </div>
                      <div className="survey-submission-meta">
                        <span>{answeredCount}/{questions.length} válasz</span>
                        <small>{formatDateTime(response.submittedAt)}</small>
                      </div>
                      <div className="survey-submission-action">
                        <FontAwesomeIcon icon={faEye} />
                      </div>
                    </button>
                  );
                })}
                {!pagedResponses.length ? (
                  <div className="survey-empty-state">Nincs találat.</div>
                ) : null}
              </div>

              <div className="survey-pagination">
                <button
                  className="survey-pagination-button"
                  onClick={() => setResponsePage((page) => Math.max(1, page - 1))}
                  disabled={responsePage === 1}
                >
                  Előző
                </button>
                <span>
                  {responsePage} / {responsePageCount}
                </span>
                <button
                  className="survey-pagination-button"
                  onClick={() => setResponsePage((page) => Math.min(responsePageCount, page + 1))}
                  disabled={responsePage === responsePageCount}
                >
                  Következő
                </button>
              </div>
            </div>

            <aside className="survey-stats-panel">
              <SummaryCard
                icon={faUsers}
                label="Beküldések"
                value={responses.length}
                helper="Kitöltött kérdőív"
              />
              <SummaryCard
                icon={faStar}
                label="Átlag"
                value={averageScaleScore === null ? "-" : averageScaleScore.toFixed(1)}
                helper="Skálás válaszokból"
              />
              <SummaryCard
                icon={faChartBar}
                label="Kitöltöttség"
                value={`${completionRate}%`}
                helper="Válaszolt mezők aránya"
              />
              <SummaryCard
                icon={faFileLines}
                label="Kérdések"
                value={questions.length}
                helper="Vegyes kérdéstípusok"
              />
            </aside>
          </div>
        </Fragment>
      )}

      <SurveyDetailsSheet
        open={isSheetOpen}
        onClose={() => {
          setSheetResponse(null);
          setIsTemplateSheetOpen(false);
        }}
        overview={surveyOverview}
        selectedResponse={sheetResponse}
      />
    </section>
  );
}
